/**
 * The one display decision that belongs to a search hit rather than to a patient.
 *
 * Everything else the result rows show — name, identifier, age, safety badges — is in
 * `patientDisplay.ts` and is shared with the ward list. What is here is the column the
 * two screens do not have in common: the ward list knows where a patient is because
 * that is what it is a list of, and a search has to say.
 *
 * Its own module rather than a function inside `PatientSearch.tsx` so that it can be
 * tested without a DOM, which is how every other display decision in this frontend is
 * covered.
 */

import type { TFunction } from 'i18next'

import type { PatientSearchHit } from './patientApi'

/** The lookup the search screen needs. `common` first — see `patientDisplay.ts`. */
export type PatientSearchTranslate = TFunction<['common', 'patient']>

/** Enough of a hit to say where the patient is. */
export type PatientLocation = Pick<PatientSearchHit, 'ward' | 'bed'>

/**
 * Where the patient is, or that they are not admitted.
 *
 * **Not admitted is a real answer and a common one** — most of the record is people
 * who are not currently in a bed, and every ballast patient is one — so it is a
 * sentence rather than an empty cell, which would read as a rendering fault.
 *
 * The ward and the bed go through one message each way round. A ward with no bed
 * happens (an admission recorded before a bed was assigned), and gluing a translated
 * *plats* between two values is the concatenation I18N.md §8 forbids: the word order
 * is the translator's decision, not this function's.
 *
 * The ward code is shown as recorded. It is what a clinician reads off the printed
 * sheet and what `--seed-demo` created, so it is not mapped to anything.
 */
export function patientLocation(hit: PatientLocation, t: PatientSearchTranslate): string {
  if (hit.ward === null) {
    return t('patient:location.notAdmitted')
  }

  return hit.bed === null
    ? t('patient:location.wardOnly', { ward: hit.ward })
    : t('patient:location.wardAndBed', { ward: hit.ward, bed: hit.bed })
}
